import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AuthContext } from '../context/AuthContext';
import { getDevicesWithUpdates, updateDeviceFirmwares, getCustomers } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faArrowUp, faArrowDown, faArrowsUpDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { formatRelativeDate } from '../utils/date';
import { ROLE_SUPERADMIN } from '../utils/roles';
import Modal from '../components/Modal';
import DeviceBatteryRssiChart from '../components/DeviceBatteryRssiChart';
import useModalState from '../hooks/useModalState';

const DevicesPage = () => {
        const { user } = useContext(AuthContext);
        const [devices, setDevices] = useState([]);
        const [customers, setCustomers] = useState({});
        const [availableFirmware, setAvailableFirmware] = useState([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [firmwareVersions, setFirmwareVersions] = useState({});
        const [sortField, setSortField] = useState('id');
        const [sortDirection, setSortDirection] = useState('asc');
        const [selectedDevices, setSelectedDevices] = useState(new Set());
        const [bulkFirmware, setBulkFirmware] = useState('');
        const { showToast } = useToast();
        const chartModal = useModalState();

        useEffect(() => {
                fetchData();
        }, []);

        const fetchData = async () => {
                try {
                        const [devicesResponse, customersResponse] = await Promise.all([
                                getDevicesWithUpdates(),
                                getCustomers()
                        ]);

                        if (!devicesResponse.ok || !customersResponse.ok) {
                                throw new Error('Failed to fetch data');
                        }

                        const devicesData = await devicesResponse.json();
                        const customersData = await customersResponse.json();

                        // Create customer lookup map
                        const customerMap = {};
                        (customersData.data || []).forEach(c => {
                                customerMap[c.id] = c.name;
                        });

                        setDevices(devicesData.data.devices || []);
                        setAvailableFirmware(devicesData.data.fw || []);
                        setCustomers(customerMap);
                } catch (err) {
                        setError(err.message);
                } finally {
                        setLoading(false);
                }
        };

        const handleFirmwareChange = (deviceId, value) => {
                setFirmwareVersions(prev => ({
                        ...prev,
                        [deviceId]: value
                }));
        };

        const handleCancelUpgrade = (deviceId) => {
                setFirmwareVersions(prev => ({
                        ...prev,
                        [deviceId]: ''
                }));
        };

        const handleSelectDevice = (deviceId) => {
                setSelectedDevices(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(deviceId)) {
                                newSet.delete(deviceId);
                        } else {
                                newSet.add(deviceId);
                        }
                        return newSet;
                });
        };

        const handleSelectAll = () => {
                if (selectedDevices.size === sortedDevices.length) {
                        setSelectedDevices(new Set());
                } else {
                        setSelectedDevices(new Set(sortedDevices.map(d => d.id)));
                }
        };

        const handleBulkApply = () => {
                if (!bulkFirmware || selectedDevices.size === 0) return;
                const updates = {};
                selectedDevices.forEach(deviceId => {
                        updates[deviceId] = bulkFirmware;
                });
                setFirmwareVersions(prev => ({ ...prev, ...updates }));
                setSelectedDevices(new Set());
                setBulkFirmware('');
        };

        const handleSubmit = async () => {
                const updates = Object.keys(firmwareVersions)
                        .filter(deviceId => firmwareVersions[deviceId] || firmwareVersions[deviceId] === '')
                        .map(deviceId => {
                                const device = devices.find(d => d.id === parseInt(deviceId));
                                return {
                                        id: device?.device_update?.id || 0,
                                        device_id: parseInt(deviceId),
                                        requested_firmware: firmwareVersions[deviceId]
                                };
                        });

                if (updates.length === 0) {
                        showToast('error', 'Please select firmware versions to update');
                        return;
                }

                try {
                        const response = await updateDeviceFirmwares(updates);
                        if (!response.ok) {
                                throw new Error('Failed to update firmware');
                        }
                        showToast('success', 'Firmware updates saved successfully');
                        fetchData();
                        setFirmwareVersions({});
                } catch (err) {
                        showToast('error', err.message);
                }
        };

        // battery_level is always a number, so 0 is ambiguous: a flat cell or a
        // device that has never reported. updated_at (SensorsUpdatedAt) is the
        // discriminator -- it is null until the first sample arrives.
        const hasReported = (device) => device.updated_at != null;

        const getCustomerName = (device) => {
                return device.customer || '-';
        };

        // Organization names can be long enough to force the row wider than the
        // viewport. Clip the display and keep the full name in the native tooltip.
        const ORG_NAME_MAX_CHARS = 20;

        const clipOrgName = (name) => (
                name.length > ORG_NAME_MAX_CHARS ? `${name.slice(0, ORG_NAME_MAX_CHARS)}...` : name
        );

        const getStatus = (device) => {
                if (!device.device_update || !device.device_update.requested_firmware) {
                        return 'OK';
                }
                if (device.firmware_version !== device.device_update.requested_firmware) {
                        return 'Upgrade Requested';
                }
                return 'OK';
        };

        const handleSort = (field) => {
                if (sortField === field) {
                        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                        setSortField(field);
                        setSortDirection('asc');
                }
        };

        const getSortIcon = (field) => {
                if (sortField !== field) return faArrowsUpDown;
                return sortDirection === 'asc' ? faArrowUp : faArrowDown;
        };

        const sortedDevices = useMemo(() => {
                const sorted = [...devices].sort((a, b) => {
                        let aValue, bValue;

                        switch (sortField) {
                                case 'id':
                                        aValue = a.id || 0;
                                        bValue = b.id || 0;
                                        break;
                                case 'hardware_version':
                                        aValue = a.hardware_version || '';
                                        bValue = b.hardware_version || '';
                                        break;
                                case 'organization':
                                        aValue = getCustomerName(a);
                                        bValue = getCustomerName(b);
                                        break;
                                case 'firmware_version':
                                        aValue = a.firmware_version || '';
                                        bValue = b.firmware_version || '';
                                        break;
                                case 'updated_at':
                                        aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
                                        bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
                                        break;
                                case 'battery_level':
                                        // Sentinels, not `|| 0`: a genuine 0% must not sort as "unknown".
                                        aValue = hasReported(a) ? a.battery_level : -1;
                                        bValue = hasReported(b) ? b.battery_level : -1;
                                        break;
                                case 'rssi':
                                        // 0 means never reported; sort it below the weakest real signal.
                                        aValue = hasReported(a) && a.rssi ? a.rssi : -999;
                                        bValue = hasReported(b) && b.rssi ? b.rssi : -999;
                                        break;
                                case 'status':
                                        aValue = getStatus(a);
                                        bValue = getStatus(b);
                                        break;
                                default:
                                        return 0;
                        }

                        if (typeof aValue === 'string') {
                                const comparison = aValue.localeCompare(bValue);
                                return sortDirection === 'asc' ? comparison : -comparison;
                        }

                        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                });

                return sorted;
        }, [devices, sortField, sortDirection]);

        if (user?.roleId !== ROLE_SUPERADMIN) {
                return <Navigate to="/" replace />;
        }

        if (loading) {
                return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        }

        if (error) {
                return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error}</div>;
        }

        return (
                <div className="page-gradient min-h-screen">
                        <Header />

                        <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                                <section className="mb-8 flex items-center justify-between">
                                        <div>
                                                <h1 className="text-3xl font-bold text-text-strong mb-2">All Devices</h1>
                                                <p className="text-text-weak">Manage firmware updates for all devices</p>
                                        </div>
                                        <button
                                                onClick={handleSubmit}
                                                data-testid="devices-save-updates"
                                                disabled={Object.keys(firmwareVersions).length === 0}
                                                className={`inline-flex items-center px-4 py-2 rounded-xl transition-colors ${Object.keys(firmwareVersions).length === 0
                                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        : 'bg-brand text-white hover:bg-brand/90'
                                                        }`}
                                        >
                                                Save Updates
                                        </button>
                                </section>

                                {/* Bulk Action Bar */}
                                {selectedDevices.size > 0 && (
                                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-4">
                                                <span data-testid="bulk-count" className="text-sm text-blue-800 font-medium">
                                                        {selectedDevices.size} device{selectedDevices.size !== 1 ? 's' : ''} selected
                                                </span>
                                                <select
                                                        data-testid="bulk-firmware"
                                                        value={bulkFirmware}
                                                        onChange={(e) => setBulkFirmware(e.target.value)}
                                                        className="px-3 py-1.5 border rounded-lg text-sm"
                                                >
                                                        <option value="">Select firmware...</option>
                                                        {availableFirmware.filter(fw => {
                                                                const selectedHwVersions = new Set(
                                                                        devices.filter(d => selectedDevices.has(d.id)).map(d => d.hardware_version)
                                                                );
                                                                return selectedHwVersions.size === 1 && selectedHwVersions.has(fw.hardware);
                                                        }).map(fw => (
                                                                <option key={fw.id} value={fw.version}>
                                                                        {fw.version}
                                                                </option>
                                                        ))}
                                                </select>
                                                <button
                                                        onClick={handleBulkApply}
                                                        data-testid="bulk-apply"
                                                        disabled={!bulkFirmware}
                                                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${bulkFirmware
                                                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                                }`}
                                                >
                                                        Apply to Selected
                                                </button>
                                                <button
                                                        onClick={() => setSelectedDevices(new Set())}
                                                        data-testid="bulk-clear"
                                                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                                                >
                                                        Clear Selection
                                                </button>
                                        </div>
                                )}

                                {/* overflow-x-auto, not overflow-hidden: the row is wider than the
                                    card on narrow viewports, and hiding the overflow clipped the last
                                    column ("Upgrade Requested" rendered as "Upgrade Re"). */}
                                <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-card overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                        <tr>
                                                                <th className="px-4 py-3 text-left">
                                                                        <input
                                                                                type="checkbox"
                                                                                checked={selectedDevices.size === sortedDevices.length && sortedDevices.length > 0}
                                                                                data-testid="devices-select-all"
                                                                                onChange={handleSelectAll}
                                                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                                        />
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('id')}
                                                                        data-testid="sort-id"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>ID</span>
                                                                                {sortField === 'id' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('id')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('hardware_version')}
                                                                        data-testid="sort-hardware-version"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>HW Rev.</span>
                                                                                {sortField === 'hardware_version' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('hardware_version')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('organization')}
                                                                        data-testid="sort-organization"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Organization</span>
                                                                                {sortField === 'organization' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('organization')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('firmware_version')}
                                                                        data-testid="sort-firmware-version"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Cur. FW</span>
                                                                                {sortField === 'firmware_version' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('firmware_version')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('updated_at')}
                                                                        data-testid="sort-updated-at"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Last Updated</span>
                                                                                {sortField === 'updated_at' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('updated_at')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('battery_level')}
                                                                        data-testid="sort-battery-level"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Bat</span>
                                                                                {sortField === 'battery_level' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('battery_level')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('rssi')}
                                                                        data-testid="sort-rssi"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>RSSI</span>
                                                                                {sortField === 'rssi' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('rssi')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Firmware</th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('status')}
                                                                        data-testid="sort-status"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Status</span>
                                                                                {sortField === 'status' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('status')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                        </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                        {sortedDevices.map(device => {
                                                                const status = getStatus(device);
                                                                return (
                                                                        <tr key={device.id} data-testid="device-row" data-device-id={device.id} className={selectedDevices.has(device.id) ? 'bg-blue-50' : ''}>
                                                                                <td className="px-4 py-4 whitespace-nowrap">
                                                                                        <input
                                                                                                type="checkbox"
                                                                                                checked={selectedDevices.has(device.id)}
                                                                                                data-testid="device-select"
                                                                                                onChange={() => handleSelectDevice(device.id)}
                                                                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                                                        />
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                                        {device.id}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                                        {device.hardware_version || '-'}
                                                                                </td>
                                                                                <td
                                                                                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                                                                        title={getCustomerName(device).length > ORG_NAME_MAX_CHARS ? getCustomerName(device) : undefined}
                                                                                >
                                                                                        {clipOrgName(getCustomerName(device))}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                                        {device.firmware_version || '-'}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                                        {device.updated_at ? formatRelativeDate(device.updated_at) : '-'}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                                        <button
                                                                                                type="button"
                                                                                                onClick={(e) => { e.stopPropagation(); chartModal.open(device); }}
                                                                                                className="text-brand hover:underline"
                                                                                                aria-label={`Battery and signal history for device ${device.id}`}
                                                                                        >
                                                                                                {hasReported(device) ? `${device.battery_level}%` : '-'}
                                                                                        </button>
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                                        <button
                                                                                                type="button"
                                                                                                onClick={(e) => { e.stopPropagation(); chartModal.open(device); }}
                                                                                                className="text-brand hover:underline"
                                                                                                aria-label={`Battery and signal history for device ${device.id}`}
                                                                                        >
                                                                                                {hasReported(device) && device.rssi ? `${device.rssi} dBm` : '-'}
                                                                                        </button>
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                                        <select
                                                                                                value={firmwareVersions[device.id] || device.device_update?.requested_firmware || ''}
                                                                                                data-testid="device-firmware-select"
                                                                                                onChange={(e) => handleFirmwareChange(device.id, e.target.value)}
                                                                                                className="px-2 py-1 border rounded-md text-sm w-32"
                                                                                        >
                                                                                                <option value="">Select version</option>
                                                                                                {availableFirmware.filter(fw => fw.hardware === device.hardware_version).map(fw => (
                                                                                                        <option key={fw.id} value={fw.version}>
                                                                                                                {fw.version}
                                                                                                        </option>
                                                                                                ))}
                                                                                        </select>
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                                        <div className="flex items-center gap-2">
                                                                                                {firmwareVersions[device.id] === '' ? (
                                                                                                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                                                                                                <FontAwesomeIcon icon={faTimes} className="mr-1" />
                                                                                                                Cancel Requested
                                                                                                        </span>
                                                                                                ) : (
                                                                                                        <>
                                                                                                                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${status === 'OK' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                                                                                        }`}>
                                                                                                                        <FontAwesomeIcon
                                                                                                                                icon={status === 'OK' ? faCheckCircle : faExclamationTriangle}
                                                                                                                                className="mr-1"
                                                                                                                        />
                                                                                                                        {status}
                                                                                                                </span>
                                                                                                                {status === 'Upgrade Requested' && (
                                                                                                                        <button
                                                                                                                                onClick={() => handleCancelUpgrade(device.id)}
                                                                                                                                data-testid="device-cancel-upgrade"
                                                                                                                                className="text-gray-400 hover:text-red-500 transition-colors"
                                                                                                                                title="Cancel upgrade"
                                                                                                                        >
                                                                                                                                <FontAwesomeIcon icon={faTimes} />
                                                                                                                        </button>
                                                                                                                )}
                                                                                                        </>
                                                                                                )}
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                );
                                                        })}
                                                </tbody>
                                        </table>
                                </div>

                                {devices.length === 0 && (
                                        <div className="text-center py-12">
                                                <p className="text-lg text-gray-600">No devices found.</p>
                                        </div>
                                )}

                                <Modal isOpen={chartModal.isOpen} onClose={chartModal.close} widthClass="max-w-4xl">
                                        <h2 className="text-lg font-semibold text-gray-900">
                                                Battery &amp; signal - device {chartModal.item?.id}
                                                {chartModal.item?.name ? ` (${chartModal.item.name})` : ''}
                                        </h2>
                                        <p className="text-sm text-gray-500 mb-2">Drag the slider to zoom.</p>
                                        {chartModal.item && (
                                                <DeviceBatteryRssiChart key={chartModal.item.id} deviceId={chartModal.item.id} />
                                        )}
                                </Modal>
                        </main>

                        <Footer />
                </div>
        );
};

export default DevicesPage;
